import { supabase, type FeedbackAction, type CardType } from './supabase';

// Log once if supabase is not configured
if (!supabase) {
  console.warn('[Tracking] Supabase not configured - tracking disabled. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY.');
}

/**
 * Upserts the anonymous user record.
 * Creates a new record if device_id doesn't exist, or updates last_seen_at if it does.
 * Returns true if successful, false if failed.
 */
export async function upsertAnonymousUser(deviceId: string): Promise<boolean> {
  if (!deviceId || !supabase) return false;
  
  try {
    const { error } = await supabase
      .from('anonymous_users')
      .upsert(
        {
          device_id: deviceId,
          last_seen_at: new Date().toISOString(),
        },
        {
          onConflict: 'device_id',
          // Don't overwrite created_at or total_generations on update
          ignoreDuplicates: false,
        }
      );
    
    if (error) {
      console.error('[Tracking] Failed to upsert anonymous user:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[Tracking] Error upserting anonymous user:', err);
    return false;
  }
}

/**
 * Increments the total_generations count for a user.
 * The RPC handles creating the user if they don't exist.
 */
export async function incrementGenerations(deviceId: string): Promise<void> {
  if (!deviceId || !supabase) return;
  
  try {
    // Note: increment_generations RPC creates user if not exists
    const { error } = await supabase.rpc('increment_generations', {
      p_device_id: deviceId,
    });
    
    if (error) {
      console.error('[Tracking] Failed to increment generations:', error);
    } else {
      console.log(`[Tracking] Incremented generations for device ${deviceId.slice(0, 8)}...`);
    }
  } catch (err) {
    console.error('[Tracking] Error incrementing generations:', err);
  }
}

/**
 * Records a feedback action for a specific card.
 */
export async function recordFeedback(
  deviceId: string,
  cardId: string,
  action: FeedbackAction,
  editDiff?: Record<string, unknown> | null
): Promise<void> {
  if (!deviceId || !cardId || !supabase) return;
  
  try {
    const { error } = await supabase
      .from('user_feedback')
      .insert({
        device_id: deviceId,
        card_id: cardId,
        action,
        edit_diff: editDiff || null,
      });
    
    if (error) {
      console.error('[Tracking] Failed to record feedback:', error);
    }
  } catch (err) {
    console.error('[Tracking] Error recording feedback:', err);
  }
}

/**
 * Records multiple feedback actions in a batch.
 * Useful for tracking multiple card interactions at once (e.g., on export).
 */
export async function recordFeedbackBatch(
  deviceId: string,
  feedbacks: Array<{
    cardId: string;
    action: FeedbackAction;
    editDiff?: Record<string, unknown> | null;
  }>
): Promise<void> {
  if (!deviceId || feedbacks.length === 0 || !supabase) return;
  
  try {
    const { error } = await supabase
      .from('user_feedback')
      .insert(
        feedbacks.map((f) => ({
          device_id: deviceId,
          card_id: f.cardId,
          action: f.action,
          edit_diff: f.editDiff || null,
        }))
      );
    
    if (error) {
      console.error('[Tracking] Failed to record feedback batch:', error);
    }
  } catch (err) {
    console.error('[Tracking] Error recording feedback batch:', err);
  }
}

/**
 * Stores a generated card in the database.
 * Ensures the user exists first (FK constraint).
 */
export async function storeGeneratedCard(
  deviceId: string,
  cardId: string,
  cardType: CardType,
  content: {
    front?: string | null;
    back?: string | null;
    clozeText?: string | null;
    sourceContext?: string | null;
  }
): Promise<void> {
  if (!deviceId || !cardId || !supabase) return;
  
  try {
    // Ensure user exists before storing card (FK constraint)
    await upsertAnonymousUser(deviceId);
    
    const { error } = await supabase
      .from('generated_cards')
      .insert({
        card_id: cardId,
        device_id: deviceId,
        card_type: cardType,
        front: content.front || null,
        back: content.back || null,
        cloze_text: content.clozeText || null,
        source_context: content.sourceContext || null,
      });
    
    if (error) {
      console.error('[Tracking] Failed to store generated card:', error);
    }
  } catch (err) {
    console.error('[Tracking] Error storing generated card:', err);
  }
}

/**
 * Stores multiple generated cards in a batch.
 * Ensures the user exists first (FK constraint).
 */
export async function storeGeneratedCardsBatch(
  deviceId: string,
  cards: Array<{
    cardId: string;
    cardType: CardType;
    front?: string | null;
    back?: string | null;
    clozeText?: string | null;
    sourceContext?: string | null;
  }>
): Promise<void> {
  if (!deviceId || cards.length === 0 || !supabase) {
    if (!supabase) console.warn('[Tracking] Cannot store cards - Supabase not configured');
    return;
  }
  
  try {
    // Ensure user exists before storing cards (FK constraint)
    const userCreated = await upsertAnonymousUser(deviceId);
    if (!userCreated) {
      console.error('[Tracking] Cannot store cards - failed to create/update user');
      return;
    }
    
    const { error } = await supabase
      .from('generated_cards')
      .insert(
        cards.map((c) => ({
          card_id: c.cardId,
          device_id: deviceId,
          card_type: c.cardType,
          front: c.front || null,
          back: c.back || null,
          cloze_text: c.clozeText || null,
          source_context: c.sourceContext || null,
        }))
      );
    
    if (error) {
      // Only log if it's a real error (not empty or table-not-found)
      if (error.message && !error.message.includes('generated_cards')) {
        console.error('[Tracking] Failed to store generated cards batch:', error.message);
      }
    }
  } catch {
    // Silently ignore - table may not exist yet
  }
}

/**
 * Submits a testimonial from a user.
 * Returns true if successful, false otherwise.
 */
export async function submitTestimonial(
  deviceId: string | null,
  submission: {
    testimonialText: string;
    rating?: number | null;
    email?: string | null;
    publicPermission?: boolean;
    displayName?: string | null;
    studentContext?: string | null;
    sourceJobId?: string | null;
    userId?: string | null;
  },
): Promise<boolean> {
  if (!supabase) {
    console.warn('[Tracking] Cannot submit testimonial - Supabase not configured');
    return false;
  }
  
  if (!submission.testimonialText.trim()) {
    console.warn('[Tracking] Cannot submit empty testimonial');
    return false;
  }
  
  try {
    // Ensure anonymous user exists if deviceId provided (for FK constraint)
    if (deviceId) {
      await upsertAnonymousUser(deviceId);
    }
    
    const { error } = await supabase
      .from('testimonials')
      .insert({
        device_id: deviceId || null,
        user_id: submission.userId || null,
        testimonial_text: submission.testimonialText.trim(),
        rating: submission.rating || null,
        email: submission.email?.trim() || null,
        public_permission: Boolean(submission.publicPermission),
        display_name: submission.publicPermission ? submission.displayName?.trim() || null : null,
        student_context: submission.publicPermission ? submission.studentContext?.trim() || null : null,
        source_job_id: submission.sourceJobId || null,
      });
    
    if (error) {
      console.error('[Tracking] Failed to submit testimonial:', error);
      return false;
    }
    
    console.log('[Tracking] Testimonial submitted successfully');
    return true;
  } catch (err) {
    console.error('[Tracking] Error submitting testimonial:', err);
    return false;
  }
}
